import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = DialogPrimitive.Portal;

const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
      'data-[state=open]:animate-sheet-overlay-in data-[state=closed]:animate-sheet-overlay-out',
      className
    )}
    {...props}
  />
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

const sheetSlideClasses = {
  right: {
    panel: 'inset-y-0 right-0 w-full max-w-md border-l data-[state=open]:animate-sheet-slide-in-right data-[state=closed]:animate-sheet-slide-out-right',
  },
  left: {
    panel: 'inset-y-0 left-0 w-full max-w-md border-r data-[state=open]:animate-sheet-slide-in-left data-[state=closed]:animate-sheet-slide-out-left',
  },
};

const SheetContent = React.forwardRef(({ className, children, side = 'right', ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed z-50 flex h-full flex-col border-[var(--ba-surface-border)] bg-[var(--ba-surface-bg)] shadow-xl pt-[env(safe-area-inset-top,0px)]',
        sheetSlideClasses[side]?.panel || sheetSlideClasses.right.panel,
        className
      )}
      {...props}
    >
      {children}
      <SheetClose className="absolute right-4 top-[calc(1rem+env(safe-area-inset-top,0px))] rounded-md p-1 text-[var(--ba-muted)] transition-colors hover:bg-black/5 hover:text-[var(--ba-text)] dark:hover:bg-white/5">
        <X className="h-4 w-4" />
        <span className="sr-only">Chiudi</span>
      </SheetClose>
    </DialogPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = DialogPrimitive.Content.displayName;

function SheetHeader({ className, ...props }) {
  return <div className={cn('flex flex-col gap-1.5 pr-8', className)} {...props} />;
}

function SheetFooter({ className, ...props }) {
  return <div className={cn('mt-auto flex flex-col-reverse gap-2 border-t border-[var(--ba-surface-border)] pt-4 sm:flex-row sm:justify-end', className)} {...props} />;
}

function SheetTitle({ className, ...props }) {
  return (
    <DialogPrimitive.Title
      className={cn('text-lg font-bold leading-none', className)}
      {...props}
    />
  );
}

function SheetDescription({ className, ...props }) {
  return (
    <DialogPrimitive.Description className={cn('text-sm ba-muted', className)} {...props} />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetPortal,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
